import { useInstantSearch } from 'react-instantsearch';
import { defineMessages } from 'react-intl';
import styled, { createGlobalStyle } from 'styled-components';

import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import {
  LeftFilters,
  SearchLayoutContainer,
  SearchLayoutMain,
  VerticalScroll,
} from '@sorare/core/src/components/search/SearchLayout/ui';
import { Highlightable } from '@sorare/core/src/contexts/highlight/Highlightable';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MarketVideoTutorial } from '#src/components/tutorial/MarketVideoTutorial/index.tsx';
import { ActiveFilters } from '#src/search/ActiveFilters/index.tsx';
import { FiltersManager } from '#src/search/FiltersManager/index.tsx';
import { FiltersManagerDialog } from '#src/search/FiltersManagerDialog/index.tsx';
import { SavedFilters } from '#src/search/SavedFilters/index.tsx';
import { SearchBox } from '#src/search/SearchBox/index.tsx';
import { ToggleFiltersManager } from '#src/search/ToggleFiltersManager/index.tsx';
import { CardResultsFromGraphQL } from '#src/searchCards/CardResultsFromGraphQL/index.tsx';
import { Segment } from '#src/searchCards/Segment/index.tsx';
import { SortCards } from '#src/searchCards/SortCards/index.tsx';
import { StackedSwitch } from '#src/searchCards/StackedSwitch/index.tsx';
import { TotalResults } from '#src/searchCards/TotalResults/index.tsx';

import type { Props } from './types.ts';

export const messages = defineMessages({
  defaultPlaceholder: {
    id: 'AdvancedCardSearch.placeholder',
    defaultMessage: 'Filter by player, team or rarity',
  },
  rarityCardsPlaceholder: {
    id: 'AdvancedCardSearch.rarityCardsPlaceholder',
    defaultMessage: 'Filter by player or team',
  },
  playerPlaceholder: {
    id: 'AdvancedCardSearch.playerPlaceholder',
    defaultMessage: 'Filter cards of {name} by rarity or team',
  },
  teamPlaceholder: {
    id: 'AdvancedCardSearch.teamPlaceholder',
    defaultMessage: 'Filter cards of {name} by player or rarity',
  },
  leaguePlaceholder: {
    id: 'AdvancedCardSearch.leaguePlaceholder',
    defaultMessage: 'Filter cards of {name} by player, team or rarity',
  },
  countryPlaceholder: {
    id: 'AdvancedCardSearch.countryPlaceholder',
    defaultMessage: 'Filters cards of {name} by player, team or rarity',
  },
  stackPlaceholder: {
    id: 'AdvancedCardSearch.stackPlaceholder',
    defaultMessage: 'Filters cards of {name}',
  },
  favoriteCardsPlaceholder: {
    id: 'AdvancedCardSearch.favoriteCardsPlaceholder',
    defaultMessage: 'Filters favorite cards by player, team or rarity',
  },
  instantBuy: {
    id: 'AdvancedCardSearch.instantBuy',
    defaultMessage: 'Filter by player or team',
  },
});

const NoSmoothScrollStyle = createGlobalStyle`
  html {
    /* overrides the default scroll behavior we have in global style.css */
    scroll-behavior: initial;
  }
`;

const Cards = styled.div`
  position: relative;
  border-radius: 8px;
`;

const SortAndSegment = styled(Vertical).attrs({ gap: 1.5 })`
  @media ${laptopAndAbove} {
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-start;
  }
`;
const SortCtn = styled(Horizontal)`
  width: 100%;
  padding-bottom: var(--intermediate-unit);
  border-bottom: 1px solid rgba(var(--c-rgb-white), 0.11);
  @media ${laptopAndAbove} {
    width: auto;
    padding-bottom: 0;
    border-bottom: none;
  }
`;
const SearchCtn = styled(Vertical).attrs({ gap: 2 })`
  padding: 0 0 var(--double-unit);
`;

const SearchRow = styled(Horizontal).attrs({ gap: 1.5 })`
  justify-content: space-between;
`;

const ToggleFiltersPlaceholder = styled.div`
  height: var(--quadruple-unit);
`;

const ToggleFiltersWrapper = styled(Vertical).attrs({ gap: 2, center: true })`
  position: fixed;
  z-index: 2;
  bottom: var(--triple-unit);
  left: var(--double-unit);
  right: var(--double-unit);
  justify-content: flex-start;
`;

const FiltersManagerWrapper = styled.div`
  margin-bottom: var(--double-unit);
`;

export const SearchLayout = (props: Props) => {
  const { up: isDesktopOrAbove } = useScreenSize('desktop');
  const {
    topic,
    stackableTypes,
    hideSavedFilters = false,
    hideStackedSwitch = false,
    alwaysShowFavoriteButton = false,
    editableLists = false,
    cardFilters,
    sorts = ['Newly Listed', 'Ending Soon', 'Highest Price', 'Lowest Price'],
    children,
    hideOwner = false,
    galleryOwnerSlug,
    filtersTour = false,
    cardsTour = false,
    removeFinishedAuctions = false,
    removeEndedSingleSaleOffers = false,
    removeFinishedPrimaryBuys = false,
    banner,
    hideSorareUser,
    onlyPrimary,
    sport,
    GridComponent = CardResultsFromGraphQL,
    selectionMode,
    displayVideoTutorial,
    isOnboardingFlow,
    isNewNavigation,
    sealed,
    minimalistic,
  } = props;
  const { up: isLaptop } = useScreenSize('laptop');

  const [filtersOpen, toggleFilters] = useToggle(false);
  const { formatMessage } = useIntlContext();
  const { refresh } = useInstantSearch();

  const responsiveFilters = !isLaptop;

  const searchBoxPlaceholder = topic
    ? {
        player: messages.playerPlaceholder,
        team: messages.teamPlaceholder,
        league: messages.leaguePlaceholder,
        country: messages.countryPlaceholder,
        stack: messages.stackPlaceholder,
        favoriteCards: messages.favoriteCardsPlaceholder,
        instantBuy: messages.instantBuy,
      }[topic.type]
    : messages.defaultPlaceholder;

  const minimalisticSearchLayout = (
    <>
      <NoSmoothScrollStyle />
      <Vertical gap={2}>
        {children}
        <GridComponent
          hideOwner={hideOwner}
          galleryOwnerSlug={galleryOwnerSlug}
          removeFinishedAuctions={removeFinishedAuctions}
          removeEndedSingleSaleOffers={removeEndedSingleSaleOffers}
          removeFinishedPrimaryBuys={removeFinishedPrimaryBuys}
          topic={topic}
          hideSorareUser={hideSorareUser}
          stackableTypes={stackableTypes}
          showDesktopFilter
          alwaysShowFavoriteButton={alwaysShowFavoriteButton}
          editableLists={editableLists}
          onlyPrimary={onlyPrimary}
          sport={sport}
          selectionMode={selectionMode}
          sealed={sealed}
        />
      </Vertical>
    </>
  );
  const searchLayout = (
    <Vertical gap={3}>
      <NoSmoothScrollStyle />
      {banner}
      <SearchLayoutContainer>
        {!responsiveFilters && (
          <VerticalScroll>
            <Highlightable {...filtersTour} disabled={!filtersTour}>
              <LeftFilters visible>
                <SearchCtn>
                  <Horizontal>
                    <SearchBox
                      placeholder={formatMessage(searchBoxPlaceholder, {
                        name: topic?.label,
                      })}
                    />
                    {!!displayVideoTutorial && (
                      <MarketVideoTutorial
                        isOnboardingFlow={isOnboardingFlow}
                        isNewNavigation={isNewNavigation}
                      />
                    )}
                  </Horizontal>
                  <SearchRow>{!hideSavedFilters && <SavedFilters />}</SearchRow>
                  <SearchRow>
                    <TotalResults />
                  </SearchRow>
                </SearchCtn>
                <FiltersManager filters={cardFilters} skipClearAllButton />
              </LeftFilters>
            </Highlightable>
          </VerticalScroll>
        )}
        <SearchLayoutMain>
          <SortAndSegment>
            <SortCtn>
              {!isLaptop && (
                <SearchBox
                  placeholder={formatMessage(searchBoxPlaceholder, {
                    name: topic?.label,
                  })}
                />
              )}
              {!!stackableTypes?.length && !hideStackedSwitch && (
                <StackedSwitch />
              )}
              {!!isLaptop && <SortCards indexes={sorts} />}
              {!isLaptop && !hideSavedFilters && <SavedFilters />}
              {!isLaptop && !!displayVideoTutorial && (
                <MarketVideoTutorial isOnboardingFlow={isOnboardingFlow} />
              )}
            </SortCtn>
            <Segment />
          </SortAndSegment>
          {children}
          <ActiveFilters />
          <Highlightable {...cardsTour} position="left" disabled={!cardsTour}>
            <Cards>
              <GridComponent
                hideOwner={hideOwner}
                galleryOwnerSlug={galleryOwnerSlug}
                removeFinishedAuctions={removeFinishedAuctions}
                removeEndedSingleSaleOffers={removeEndedSingleSaleOffers}
                removeFinishedPrimaryBuys={removeFinishedPrimaryBuys}
                topic={topic}
                hideSorareUser={hideSorareUser}
                stackableTypes={stackableTypes}
                showDesktopFilter
                alwaysShowFavoriteButton={alwaysShowFavoriteButton}
                editableLists={editableLists}
                onlyPrimary={onlyPrimary}
                sport={sport}
                selectionMode={selectionMode}
                sealed={sealed}
              />
            </Cards>
          </Highlightable>
        </SearchLayoutMain>
      </SearchLayoutContainer>
      {!!responsiveFilters && (
        <>
          {!isLaptop ? (
            <Portal id="above-bottom-bar-portal">
              <FiltersManagerWrapper>
                <Highlightable {...filtersTour} disabled={!filtersTour}>
                  <ToggleFiltersManager toggleFilters={toggleFilters} />
                </Highlightable>
              </FiltersManagerWrapper>
            </Portal>
          ) : (
            <ToggleFiltersPlaceholder>
              <ToggleFiltersWrapper>
                <Highlightable {...filtersTour} disabled={!filtersTour}>
                  <ToggleFiltersManager toggleFilters={toggleFilters} />
                </Highlightable>
              </ToggleFiltersWrapper>
            </ToggleFiltersPlaceholder>
          )}
          <FiltersManagerDialog
            open={filtersOpen}
            onClose={toggleFilters}
            filtersGroups={cardFilters}
            sorts={sorts}
          />
        </>
      )}
    </Vertical>
  );
  if (isDesktopOrAbove) {
    if (minimalistic) return minimalisticSearchLayout;
    return searchLayout;
  }
  return (
    <PullToRefetch refetch={async () => refresh()}>
      {minimalistic ? minimalisticSearchLayout : searchLayout}
    </PullToRefetch>
  );
};
